import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectIsReportView } from 'util/selector/reportSelector';

import { getAppConfig } from 'app/getConfig';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useWBIntercom } from '@workbuzz/shared-ui';
import { userApi } from 'container/userApi';
import {
  selectUserId,
  selectUserEmail,
  selectUserFirstName,
  selectUserLastName,
  selectUserExpiration,
} from 'reduxStore/user/selectors';
import { FeatureFlagEnum } from 'shared/constants/featureFlagEnum';

export const useInitializeIntercom = () => {
  const { INTERCOM_APP_ID, INTERCOM_MESSENGER_STYLE_ID } = getAppConfig();
  const isIntercomEnabled = useFeatureIsOn(FeatureFlagEnum.intercom);
  const isReportView = useSelector(selectIsReportView);
  const userId = useSelector(selectUserId);
  const email = useSelector(selectUserEmail);
  const firstName = useSelector(selectUserFirstName);
  const lastName = useSelector(selectUserLastName);
  const expiration = useSelector(selectUserExpiration);

  const enabled = isIntercomEnabled && !isReportView && Boolean(userId);

  const [intercomUserJwt, setIntercomUserJwt] = useState<string>();

  useEffect(() => {
    if (!enabled) {
      setIntercomUserJwt(undefined);
      return;
    }

    let active = true;
    userApi
      .fetchIntercomToken()
      .then((token) => active && setIntercomUserJwt(token))
      // failures are already reported to Datadog by the authClient interceptor
      .catch(() => active && setIntercomUserJwt(undefined));

    return () => {
      active = false;
    };
    // re-fetch on session change; assumes the JWT TTL is >= the session TTL (no mid-session expiry)
  }, [enabled, expiration]);

  const name = [firstName, lastName].filter(Boolean).join(' ') || undefined;

  // boot only once the identity JWT is present, so the Messenger never runs unverified
  useWBIntercom({
    enabled: enabled && Boolean(intercomUserJwt),
    appId: INTERCOM_APP_ID,
    messengerStyleId: INTERCOM_MESSENGER_STYLE_ID || undefined,
    user: userId
      ? {
          userId,
          email,
          name,
          userJwt: intercomUserJwt,
        }
      : undefined,
  });
};
